import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { buildApiUrl, getApiKey } from '../utils/api';

/**
 * Zustand store that keeps per-wallet feature flags (beta features).
 * Flags are cached in localStorage so we avoid flashing UI on refresh.
 */
export const useFeatureFlags = create(
  persist(
    (set, get) => ({
      flags: [], // array<string>
      /**
       * Fetch flags for current authenticated wallet.
       * Requires the user to be logged in (api_key in storage).
       */
      async fetchFlags(walletAddress) {
        try {
          if (!walletAddress) return;
          const apiKey = getApiKey();
          if (!apiKey) return;

          const res = await fetch(buildApiUrl(`/admin/feature-flags/${walletAddress}`), {
            headers: { Authorization: `Bearer ${apiKey}` },
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          set({ flags: Array.isArray(data.beta_features) ? data.beta_features : [] });
        } catch (err) {
          console.error('fetchFlags error', err);
        }
      },
      /**
       * Helper: is a feature enabled?
       */
      isEnabled(feature) {
        return get().flags?.includes?.(feature);
      },
    }),
    {
      name: 'dcw_feature_flags', // localStorage key
      partialize: (state) => ({ flags: state.flags }),
    },
  ),
);

// Convenience re-export
export const isFeatureEnabled = (flag) => useFeatureFlags.getState().isEnabled(flag);
/**
 * RETIRED as the home (ADR-0020): the DopeOS desktop is always the home and
 * `?home=strip` no longer replaces it. The helper is kept (unused by the shell)
 * until street_home.js is deleted; the view stays reachable via `?view=street_home`.
 */
export function isStreetHomeEnabled() {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('home') === 'strip' || params.get('home_strip') === '1') return true;
    if (window.localStorage.getItem('dcw_feature_home_strip') === '1') return true;
  } catch {
    /* storage or URL unavailable */
  }
  return isFeatureEnabled('home_strip');
}
